import React, { useMemo, useState } from "react";
import {
  View,
  Flex,
  ActionButton,
  Button,
  Text,
  MenuTrigger,
  Menu,
  Item,
  Link,
} from "@adobe/react-spectrum";
import {
  Trash,
  Plus,
  ArrowCounterclockwise,
  Search,
  Funnel,
  ChatText,
  List,
  Archive,
} from "react-bootstrap-icons";
import {
  ReplyIcon,
  ReplyAllIcon,
  ArrowForwardIcon,
  MoreIcon,
} from "../../../../components/protected/Icons/IconsLib";
import {
  MessagesComponent,
  CommActionHandlers,
} from "./message_list/MessagesComponent";
import { useTranslation } from "react-i18next";
import { useViewInfo } from "../../../../hooks/UseViewInfo";
import { useDeviceDetection } from "../../../../hooks/UseDeviceDetection";
import styles from "./communication_list.module.css";

interface ICommunicationListProps {
  setShow: (show: boolean) => void;
  setSelectedRecordView: (
    recordView:
      | "todo"
      | "messages"
      | "sent"
      | "trash"
      | "corporate"
      | "concept"
      | "",
  ) => void;
  setShowMobileMenu?: (show: boolean) => void;
  messageType:
    | "todo"
    | "messages"
    | "sent"
    | "trash"
    | "corporate"
    | "concept"
    | "";
  customTreeHandle?: number;
  activeViewLabel?: string;
  onDesktopRowDragStart?: (message: any | null) => void;
  onRegisterMoveMessage?: (
    moveHandler:
      | ((messageId: string, newNode: string | number) => Promise<boolean>)
      | null,
  ) => void;
}

const CommunicationList: React.FC<ICommunicationListProps> = ({
  setShow,
  setSelectedRecordView,
  setShowMobileMenu,
  messageType,
  customTreeHandle,
  activeViewLabel,
  onDesktopRowDragStart,
  onRegisterMoveMessage,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useViewInfo();
  const deviceInfo = useDeviceDetection(isMobile);
  const shouldUseDesktopLayout =
    !deviceInfo.shouldUseMobileLayout() ||
    ((deviceInfo.isTablet || deviceInfo.isIPad) &&
      deviceInfo.orientation === "landscape");
  const isCompact = !shouldUseDesktopLayout;
  const useMobileCompactLayout = isCompact;
  const isTabletLandscapeDesktop =
    shouldUseDesktopLayout &&
    (deviceInfo.isTablet || deviceInfo.isIPad) &&
    deviceInfo.orientation === "landscape";
  const [isCompactDetailOpen, setIsCompactDetailOpen] = useState(false);
  const [actionHandlers, setActionHandlers] =
    useState<CommActionHandlers | null>(null);
  const phoneTitle = useMemo(() => {
    if (activeViewLabel) {
      return activeViewLabel;
    }

    switch (messageType) {
      case "messages":
        return t("My Messages", {
          ns: "layout_components",
          defaultValue: "My messages",
        });
      case "sent":
        return t("Sent", { ns: "layout_components", defaultValue: "Sent" });
      case "concept":
        return t("Concept", {
          ns: "layout_components",
          defaultValue: "Concepts",
        });
      case "trash":
        return t("Trash", { ns: "layout_components", defaultValue: "Trash" });
      case "corporate":
        return t("Corporate Messages", {
          ns: "layout_components",
          defaultValue: "Corporate Messages",
        });
      case "todo":
      default:
        return t("Todo", { ns: "layout_components", defaultValue: "Todo" });
    }
  }, [activeViewLabel, messageType, t]);

  return (
    <>
      <View
        width={"100%"}
        position={"relative"}
        height={"100%"}
        overflow={"hidden"}
        UNSAFE_className={styles.header}
      >
        {/* Phone header */}
        {useMobileCompactLayout && !isCompactDetailOpen && (
          <Flex
            direction={"column"}
            position={"absolute"}
            top={"0px"}
            left={"0px"}
            right={"0px"}
            UNSAFE_className={styles.phone_header_shell}
          >
            <Flex
              direction={"row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              UNSAFE_className={styles.phone_header_top}
            >
              <button
                onClick={() => setShow(true)}
                className={styles.phone_header_back}
                aria-label={t("View", {
                  ns: "layout_components",
                  defaultValue: "View",
                })}
              >
                <List size={16} />
                <Text>{phoneTitle}</Text>
              </button>
              <ActionButton
                isQuiet
                aria-label={t("filter", {
                  ns: "layout_components",
                  defaultValue: "Filter",
                })}
                UNSAFE_className={styles.phone_header_filter}
                onPress={() => actionHandlers?.onOpenFilter?.()}
              >
                <Funnel size={18} />
              </ActionButton>
            </Flex>
          </Flex>
        )}

        {/* Desktop action bar — above tab strip */}
        {!isCompact && (
          <Flex
            direction={"row"}
            position={"absolute"}
            top={"0px"}
            left={"0px"}
            right={"0px"}
            height={"64px"}
            alignItems={"center"}
            UNSAFE_style={{
              backgroundColor: "#fff",
              borderTop: "1px solid #eceff1",
              borderBottom: "1px solid #eceff1",
              zIndex: 1,
            }}
          >
            {/* Left section: aligned with list panel (~35%) */}
            <Flex
              direction={"row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              UNSAFE_style={{
                width: "35.3%",
                height: "100%",
                paddingLeft: "16px",
                paddingRight: "16px",
                borderRight: "1px solid #dee4e7",
                boxSizing: "border-box",
                flexShrink: 0,
              }}
            >
              <Flex direction={"row"} alignItems={"center"} gap={"size-150"}>
                {isTabletLandscapeDesktop && (
                  <ActionButton
                    isQuiet
                    onPress={() => setShow(true)}
                    aria-label={t("Views", {
                      ns: "layout_components",
                      defaultValue: "Views",
                    })}
                    UNSAFE_className={styles.quiet_icon_btn}
                  >
                    <List size={18} />
                  </ActionButton>
                )}
                <Button
                  variant="primary"
                  UNSAFE_style={{
                    background: "transparent",
                    border: "1px solid #1976d2",
                    color: "#1976d2",
                    borderRadius: "8px",
                    fontSize: "14px",
                    whiteSpace: "nowrap",
                  }}
                  onPress={() => actionHandlers?.onNewEmail()}
                >
                  <>
                    <Plus size={16} />
                    &nbsp;{t("new_email", { ns: "layout_components" })}
                  </>
                </Button>
              </Flex>

              <Flex direction={"row"} alignItems={"center"} gap={"size-75"}>
                <button
                  className={styles.quiet_icon_btn}
                  aria-label="Refresh"
                  onClick={() => actionHandlers?.onRefresh?.()}
                >
                  <ArrowCounterclockwise size={18} />
                </button>
                <button
                  className={styles.quiet_icon_btn}
                  aria-label="Search"
                  onClick={() => actionHandlers?.onOpenSearch?.()}
                >
                  <Search size={18} />
                </button>
                <button
                  className={styles.quiet_icon_btn}
                  aria-label="Filter"
                  onClick={() => actionHandlers?.onOpenFilter?.()}
                >
                  <Funnel size={18} />
                </button>
                <button
                  className={styles.quiet_icon_btn}
                  aria-label="More options"
                >
                  <MoreIcon size={18} />
                </button>
              </Flex>
            </Flex>
            {/* Right section: Reply/ReplyAll/Forward | Comment + Delete/More */}
            <Flex
              direction={"row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              UNSAFE_style={{
                flex: 1,
                height: "100%",
                paddingLeft: "16px",
                paddingRight: "16px",
              }}
            >
              {/* Reply / Reply All / Forward | Comment */}
              <Flex direction={"row"} alignItems={"center"} gap={"size-300"}>
                {actionHandlers?.showReplyAction && (
                  <button
                    className={styles.labeled_btn}
                    onClick={() => actionHandlers?.onReply()}
                    aria-label="Reply"
                  >
                    <ReplyIcon size={16} />
                    {t("reply", {
                      ns: "layout_components",
                      defaultValue: "Reply",
                    })}
                  </button>
                )}
                {actionHandlers?.showReplyAction && (
                  <button
                    className={styles.labeled_btn}
                    onClick={() => actionHandlers?.onReplyAll()}
                    aria-label="Reply All"
                  >
                    <ReplyAllIcon size={16} />
                    {t("reply_all", {
                      ns: "layout_components",
                      defaultValue: "Reply All",
                    })}
                  </button>
                )}
                {actionHandlers?.showForwardAction && (
                  <button
                    className={styles.labeled_btn}
                    onClick={() => actionHandlers?.onForward()}
                    aria-label="Forward"
                  >
                    <ArrowForwardIcon size={16} />
                    {t("forward", {
                      ns: "layout_components",
                      defaultValue: "Forward",
                    })}
                  </button>
                )}
                {actionHandlers?.showCommentAction &&
                  (actionHandlers?.showReplyAction ||
                    actionHandlers?.showForwardAction) && (
                  <View
                    width={"1px"}
                    height={"24px"}
                    UNSAFE_style={{ backgroundColor: "#dee4e7" }}
                  />
                )}
                {actionHandlers?.showCommentAction && (
                  <button
                    className={styles.labeled_btn}
                    aria-label="Comment"
                    onClick={() => actionHandlers?.onComment?.()}
                  >
                    <ChatText size={16} />
                    {t("comment", { ns: "layout_components" })}
                  </button>
                )}
              </Flex>

              {/* Dominant CTA (Resolve or Mark as done) | Delete / More */}
              <Flex direction={"row"} alignItems={"center"} gap={"size-100"}>
                {/* {(actionHandlers?.showResolveAction ||
                  actionHandlers?.showMarkDoneAction) && (
                  <button
                    className={
                      actionHandlers?.showMarkDoneAction
                        ? styles.primary_mark_done_btn
                        : styles.primary_resolve_btn
                    }
                    onClick={() => actionHandlers?.onResolveTrigger()}
                    aria-label={
                      actionHandlers?.showMarkDoneAction
                        ? t("mark_as_done", {
                            ns: "layout_components",
                            defaultValue: "Mark as done",
                          })
                        : t("Resolve message", {
                            ns: "layout_components",
                            defaultValue: "Resolve message",
                          })
                    }
                  >
                    {actionHandlers?.showMarkDoneAction ? (
                      <Check2Circle size={16} />
                    ) : (
                      <QuestionCircle size={16} />
                    )}
                    <span>
                      {actionHandlers?.showMarkDoneAction
                        ? t("mark_as_done", {
                            ns: "layout_components",
                            defaultValue: "Mark as done",
                          })
                        : t("Resolve message", {
                            ns: "layout_components",
                            defaultValue: "Resolve message",
                          })}
                    </span>
                  </button>
                )} */}
                {(actionHandlers?.bulkSelectedCount ?? 0) > 0 && (
                  <span
                    className={styles.bulk_count_chip}
                    aria-live="polite"
                    aria-label={t("bulk_selected_count", {
                      ns: "layout_components",
                      count: actionHandlers?.bulkSelectedCount ?? 0,
                      defaultValue: "{{count}} selected",
                    })}
                  >
                    {t("bulk_selected_count", {
                      ns: "layout_components",
                      count: actionHandlers?.bulkSelectedCount ?? 0,
                      defaultValue: "{{count}} selected",
                    })}
                  </span>
                )}
                {actionHandlers?.showMoveAction && (
                  <ActionButton
                    isQuiet
                    UNSAFE_className={styles.quiet_icon_btn}
                    onPress={() => actionHandlers?.onMoveTrigger?.()}
                    aria-label={t("move", {
                      ns: "layout_components",
                      defaultValue: "Move",
                    })}
                  >
                    <Archive size={20} />
                  </ActionButton>
                )}
                {actionHandlers?.showDeleteAction && (
                  <ActionButton
                    isQuiet
                    UNSAFE_className={styles.quiet_icon_btn}
                    onPress={() => actionHandlers?.onDeleteTrigger()}
                    aria-label="Delete"
                  >
                    <Trash size={20} />
                  </ActionButton>
                )}
                {actionHandlers?.showMarkDoneAction && (
                  <MenuTrigger>
                    <Link>
                      <span aria-label="Open context menu" role="button">
                        <MoreIcon size={24} />
                      </span>
                    </Link>
                    <Menu
                      width={"220px"}
                      onAction={(key) => {
                        if (key === "markDone") {
                          actionHandlers?.onResolveTrigger();
                        }
                      }}
                    >
                      <Item key="markDone" textValue="mark as done">
                        {t("mark_as_done", {
                          ns: "layout_components",
                          defaultValue: "Mark as done",
                        })}
                      </Item>
                    </Menu>
                  </MenuTrigger>
                )}
              </Flex>
            </Flex>
          </Flex>
        )}

        {/* Messages — below action bar on desktop, below back button on mobile */}
        <View
          position={"absolute"}
          bottom={"0px"}
          top={
            useMobileCompactLayout
              ? isCompactDetailOpen
                ? "0px"
                : "56px"
              : "64px"
          }
          left={"0px"}
          width={"100%"}
          overflow={"hidden"}
        >
          <MessagesComponent
            key={`comm-list-${messageType}-${String(customTreeHandle ?? "default")}`}
            setShowMobileMenu={setShowMobileMenu}
            messageType={messageType}
            customTreeHandle={customTreeHandle}
            onRegisterHandlers={setActionHandlers}
            onRegisterMoveMessage={onRegisterMoveMessage}
            onDesktopRowDragStart={onDesktopRowDragStart}
            onDetailVisibilityChange={setIsCompactDetailOpen}
            percentHeight={100}
          />
        </View>
      </View>
    </>
  );
};

export default CommunicationList;





